import { useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ApiError, api } from "@/lib/api";
import { useAuth } from "@/state/auth";
import type { Session } from "@/lib/types";
import { Alert, Button, Field } from "@/components/ui";
import { AuthLayout } from "@/pages/Login";

/**
 * Choosing a password from a one-time link.
 *
 * Serves both doors onto the same mechanism: a forgotten password and an
 * invitation. The copy adapts, because somebody who has just been invited has
 * never had a password and should not be told they are "resetting" one.
 *
 * Signs in on success. The link already proved control of the mailbox, so a
 * login form here would ask the person to retype what they typed a second ago.
 */
export function SetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { adoptSession } = useAuth();

  const token = params.get("token") ?? "";
  const email = params.get("email") ?? "";
  // The link carries the reason so the page can read correctly for each.
  const inviting = params.get("purpose") === "invite";

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const tooShort = password.length > 0 && password.length < 10;
  const mismatch = confirm.length > 0 && confirm !== password;
  const ready = password.length >= 10 && confirm === password;

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError("");
    setBusy(true);
    try {
      const session = await api.publicPost<Session>("/v1/auth/password/set", { token, password });
      await adoptSession(session);
      navigate("/app");
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : "Could not set that password. Ask for a new link.",
      );
    } finally {
      setBusy(false);
    }
  }

  if (!token) {
    return (
      <AuthLayout title="That link is incomplete" subtitle="It may have been cut short by an email client.">
        <div className="space-y-4">
          <Alert>No token in this link, so there is nothing to verify.</Alert>
          <Link className="text-sm font-medium text-brand" to="/forgot-password">
            Ask for a new link
          </Link>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title={inviting ? "Choose your password" : "Set a new password"}
      subtitle={email ? `For ${email}.` : "Pick something only you know."}
    >
      <form onSubmit={submit} className="space-y-4">
        {error && <Alert>{error}</Alert>}
        <Field
          label="New password"
          type="password"
          name="new-password"
          required
          autoComplete="new-password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          error={tooShort ? "At least 10 characters" : undefined}
          hint={tooShort ? undefined : "At least 10 characters. Longer beats complicated."}
        />
        <Field
          label="Again, to be sure"
          type="password"
          name="confirm-password"
          required
          autoComplete="new-password"
          value={confirm}
          onChange={(event) => setConfirm(event.target.value)}
          error={mismatch ? "These do not match" : undefined}
        />
        <Button type="submit" className="w-full" loading={busy} disabled={!ready}>
          {inviting ? "Set it and sign in" : "Save and sign in"}
        </Button>
        <p className="text-center text-sm text-ink-500">
          Link expired?{" "}
          <Link className="font-medium text-brand" to="/forgot-password">
            Ask for a new one
          </Link>
        </p>
      </form>
    </AuthLayout>
  );
}
